import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { User, FileText, Bookmark, BarChart3, Trash2, Camera, Bell, Sparkles, MapPin, Briefcase, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StarRating from "@/components/StarRating";
import MultiSelectDropdown from "@/components/MultiSelectDropdown";
import JobCard from "@/components/JobCard";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";

function RecommendedJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/jobs/recommended", { params: { limit: 6 } })
      .then(r => setJobs(r.data))
      .finally(() => setLoading(false));
  }, []);
  if (!loading && jobs.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="recommended-jobs">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#1D6FF2]" />
        <h3 className="text-sm font-semibold text-slate-900">Recommended for you</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Based on your profile, saved jobs, applications, and alerts.</p>
      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => <div key={i} className="skeleton h-32 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {jobs.map(j => <JobCard key={j.id} job={j} />)}
        </div>
      )}
    </div>
  );
}

function Profile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [pct, setPct] = useState(0);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const refreshCompletion = () => api.get("/seeker/profile-completion").then(r => setPct(r.data.percent));
  useEffect(() => {
    api.get("/auth/me").then(r => {
      setMe(r.data);
      const p = r.data.profile || {};
      setForm({
        name: r.data.name, mobile: r.data.mobile || "",
        city: p.city || "", current_title: p.current_title || "",
        experience_years: p.experience_years || 0, education: p.education || "",
        skills: (p.skills || []).join(", "), languages: (p.languages || []).join(", "),
        resume: p.resume || "", photo: p.photo || "",
        preferred_categories: p.preferred_categories || [], preferred_cities: p.preferred_cities || [],
      });
    });
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/cities").then(r => setCities(r.data));
    refreshCompletion();
  }, []);
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(s => ({ ...s, resume: r.data.url }));
      toast.success("Resume uploaded");
    } catch (e) { toast.error(formatError(e)); }
  };
  const uploadPhoto = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(s => ({ ...s, photo: r.data.url }));
      // immediately save photo
      await api.put("/profile", { photo: r.data.url });
      refreshCompletion();
      toast.success("Photo updated");
    } catch (e) { toast.error(formatError(e)); }
  };
  const deletePhoto = async () => {
    try {
      await api.delete("/profile/photo");
      setForm(s => ({ ...s, photo: "" }));
      refreshCompletion();
      toast.success("Photo removed");
    } catch (e) { toast.error(formatError(e)); }
  };
  const save = async () => {
    try {
      await api.put("/profile", {
        ...form,
        skills: form.skills.split(",").map(s => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Profile saved");
      refreshCompletion();
    } catch (e) { toast.error(formatError(e)); }
  };
  if (!me) return <div>Loading...</div>;
  return (
    <div data-testid="seeker-profile">
      <h2 className="text-2xl font-bold mb-3">My Profile</h2>
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="profile-completion">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-semibold">Profile completion</div>
            <div className="text-xs text-slate-500">{pct < 75 ? `Reach 75% to apply for jobs (need ${75 - pct}% more)` : "You can apply for jobs ✓"}</div>
          </div>
          <div className={`text-2xl font-bold ${pct >= 75 ? "text-green-600" : "text-yellow-600"}`}>{pct}%</div>
        </div>
        <Progress value={pct} className={pct >= 75 ? "[&>div]:bg-green-500" : "[&>div]:bg-yellow-500"} />
      </div>

      <RecommendedJobs />

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        {/* Photo */}
        <div className="flex items-center gap-4">
          {form.photo ? (
            <img src={form.photo.startsWith("http") ? form.photo : `${process.env.REACT_APP_BACKEND_URL}${form.photo}`} alt="" className="w-20 h-20 rounded-full object-cover border" data-testid="profile-photo" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-slate-100 grid place-items-center text-slate-400"><Camera className="w-7 h-7" /></div>
          )}
          <div className="flex flex-col gap-2">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-upload" />
              <Button asChild variant="outline" size="sm"><span><Camera className="w-3.5 h-3.5 mr-1" /> {form.photo ? "Change" : "Upload"} Photo</span></Button>
            </label>
            {form.photo && (
              <Button variant="ghost" size="sm" onClick={deletePhoto} className="text-red-600" data-testid="delete-photo-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Full Name</Label><Input value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div><Label>Mobile</Label><Input value={form.mobile || ""} onChange={e => setForm({...form, mobile: e.target.value})} /></div>
          <div><Label>City</Label><Input value={form.city || ""} onChange={e => setForm({...form, city: e.target.value})} /></div>
          <div><Label>Current / Last Title</Label><Input value={form.current_title || ""} onChange={e => setForm({...form, current_title: e.target.value})} /></div>
          <div><Label>Experience (years)</Label><Input type="number" value={form.experience_years || 0} onChange={e => setForm({...form, experience_years: parseFloat(e.target.value || "0")})} /></div>
          <div><Label>Education</Label><Input value={form.education || ""} onChange={e => setForm({...form, education: e.target.value})} /></div>
          <div>
            <Label>Preferred Categories</Label>
            <MultiSelectDropdown
              icon={Briefcase}
              values={form.preferred_categories || []}
              onChange={v => setForm({ ...form, preferred_categories: v })}
              placeholder="Any category"
              searchPlaceholder="Search category..."
              options={categories.map(c => ({ value: c.name, label: c.name }))}
              triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
            />
          </div>
          <div>
            <Label>Preferred Cities</Label>
            <MultiSelectDropdown
              icon={MapPin}
              values={form.preferred_cities || []}
              onChange={v => setForm({ ...form, preferred_cities: v })}
              placeholder="Any city"
              searchPlaceholder="Search city..."
              options={cities.map(c => ({ value: c.name, label: c.name }))}
              triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
            />
          </div>
          <div className="md:col-span-2"><Label>Skills (comma separated)</Label><Input value={form.skills || ""} onChange={e => setForm({...form, skills: e.target.value})} /></div>
          <div className="md:col-span-2"><Label>Languages (comma separated)</Label><Input value={form.languages || ""} onChange={e => setForm({...form, languages: e.target.value})} /></div>
          <div className="md:col-span-2">
            <Label>Resume (PDF)</Label>
            <Input type="file" accept=".pdf" onChange={upload} data-testid="resume-upload" />
            {form.resume && <a href={`${process.env.REACT_APP_BACKEND_URL}${form.resume}`} target="_blank" rel="noreferrer" className="text-[#1D6FF2] text-sm mt-2 inline-block">View uploaded resume</a>}
          </div>
        </div>
        <Button onClick={save} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="save-profile-btn">Save Profile</Button>
      </div>
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  const [reviewedIds, setReviewedIds] = useState([]);
  const [reviewTags, setReviewTags] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [rating, setRating] = useState(0);
  const [tags, setTags] = useState([]);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/applications/mine").then(r => setApps(r.data));
    api.get("/reviews/mine").then(r => setReviewedIds(r.data)).catch(() => {});
    api.get("/reviews/tags").then(r => setReviewTags(r.data)).catch(() => {});
  }, []);

  const openReview = (a) => {
    setReviewTarget(a);
    setRating(0);
    setTags([]);
    setText("");
  };

  const toggleTag = (t) => setTags(ts => ts.includes(t) ? ts.filter(x => x !== t) : [...ts, t]);

  const submitReview = async () => {
    if (!rating) { toast.error("Pick a rating first"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { application_id: reviewTarget.id, rating, review_text: text, tags });
      toast.success("Thanks for your review!");
      setReviewedIds(ids => [...ids, reviewTarget.id]);
      setReviewTarget(null);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">My Applications</h2>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Job</TableHead><TableHead>City</TableHead><TableHead>Status</TableHead><TableHead>Applied</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {apps.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">No applications yet — start browsing!</TableCell></TableRow>}
            {apps.map(a => (
              <TableRow key={a.id}>
                <TableCell><Link to={`/jobs/${a.job?.id}`} className="text-[#1D6FF2] font-medium">{a.job?.title}</Link><div className="text-xs text-slate-500">{a.job?.business_name}</div></TableCell>
                <TableCell>{a.job?.city}</TableCell>
                <TableCell><Badge>{a.status}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{(a.created_at || "").slice(0,10)}</TableCell>
                <TableCell className="text-right">
                  {a.status === "hired" && (
                    reviewedIds.includes(a.id) ? (
                      <span className="text-xs text-green-600 font-medium whitespace-nowrap">Reviewed ✓</span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => openReview(a)} data-testid={`rate-company-${a.id}`}>
                        <Star className="w-3.5 h-3.5 mr-1" /> Rate company
                      </Button>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!reviewTarget} onOpenChange={(o) => !o && setReviewTarget(null)}>
        <DialogContent className="max-w-md" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle>Rate {reviewTarget?.job?.business_name}</DialogTitle>
            <DialogDescription>Your review helps other job seekers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Your rating</Label>
              <StarRating value={rating} onChange={setRating} size="w-7 h-7" showValue={false} />
            </div>
            <div>
              <Label className="mb-1.5 block">What stood out? (optional)</Label>
              <div className="flex flex-wrap gap-2">
                {reviewTags.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
                      tags.includes(t) ? "bg-black text-white border-black" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Review (optional)</Label>
              <Textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder="Share more about your experience..." />
            </div>
            <Button onClick={submitReview} disabled={submitting} className="w-full bg-black hover:bg-slate-800 text-white" data-testid="submit-review-btn">
              {submitting ? "Submitting..." : "Submit Review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Saved() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/saved-jobs").then(r => setItems(r.data)); }, []);
  const unsave = async (jobId) => {
    await api.delete(`/saved-jobs/${jobId}`);
    setItems(items.filter(i => i.job_id !== jobId));
  };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Saved Jobs</h2>
      <div className="space-y-3">
        {items.length === 0 && <div className="text-slate-500">No saved jobs yet.</div>}
        {items.map(s => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-4 flex justify-between items-center">
            <div>
              <Link to={`/jobs/${s.job?.id}`} className="font-semibold text-[#1D6FF2]">{s.job?.title}</Link>
              <div className="text-sm text-slate-500">{s.job?.business_name} · {s.job?.city}</div>
            </div>
            <Button variant="outline" onClick={() => unsave(s.job_id)}>Remove</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeekerStats() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/seeker/stats").then(r => setData(r.data)); }, []);
  if (!data) return <div className="skeleton h-64 rounded-xl" />;
  const STATUS_COLORS = { viewed: "#94a3b8", under_review: "#f59e0b", shortlisted: "#16a34a", not_selected: "#dc2626" };
  return (
    <div data-testid="seeker-stats">
      <h2 className="text-2xl font-bold mb-6">My Job Search Statistics</h2>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Applications</div><div className="text-3xl font-bold">{data.total_applications}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Saved Jobs</div><div className="text-3xl font-bold">{data.saved_jobs}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Profile</div><div className="text-3xl font-bold">{data.profile_completion}%</div></div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold mb-3">Application Status</h3>
          {data.by_status.length === 0 ? <p className="text-sm text-slate-500">Apply to jobs to see stats.</p> : (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={data.by_status} dataKey="count" nameKey="name" outerRadius={80} label>
                  {data.by_status.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.name] || "#1D6FF2"} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold mb-3">Best Response by City</h3>
          {data.by_city.length === 0 ? <p className="text-sm text-slate-500">Apply to jobs to see stats.</p> : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data.by_city}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1D6FF2" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 md:col-span-2">
          <h3 className="font-semibold mb-3">Applications by Category</h3>
          {data.by_category.length === 0 ? <p className="text-sm text-slate-500">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data.by_category}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#16a34a" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: "", category: "", city: "", keyword: "", salary_type: "yearly", salary_min: "", experience_min: "", experience_max: "" });

  const load = () => api.get("/alerts/mine").then(r => setAlerts(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/cities").then(r => setCities(r.data));
  }, []);

  const create = async () => {
    if (!form.category && !form.city && !form.keyword && !form.salary_min && !form.experience_min && !form.experience_max) {
      toast.error("Pick at least one criterion for the alert");
      return;
    }
    try {
      await api.post("/alerts", {
        ...form,
        salary_min: form.salary_min ? parseFloat(form.salary_min) : null,
        experience_min: form.experience_min !== "" ? parseFloat(form.experience_min) : null,
        experience_max: form.experience_max !== "" ? parseFloat(form.experience_max) : null,
      });
      toast.success("Alert created");
      setForm({ label: "", category: "", city: "", keyword: "", salary_type: "yearly", salary_min: "", experience_min: "", experience_max: "" });
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    await api.delete(`/alerts/${id}`);
    load();
  };

  const describe = (a) => [
    a.category, a.city, a.keyword && `"${a.keyword}"`,
    a.salary_min ? `${a.salary_min}+ ${a.salary_type === "monthly" ? "₹/month" : "LPA"}` : null,
    (a.experience_min != null || a.experience_max != null)
      ? `${a.experience_min ?? 0}${a.experience_max != null ? `–${a.experience_max}` : "+"} yrs exp`
      : null,
  ].filter(Boolean).join(" · ") || "Any new job from this company";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="alerts-panel">
      <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><Bell className="w-5 h-5" /> Job Alerts</h2>
      <p className="text-sm text-slate-500 mb-5">Get notified the moment a matching job goes live.</p>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Input placeholder="Label (optional) e.g. Sales jobs in Belagavi" value={form.label}
          onChange={e => setForm({ ...form, label: e.target.value })} className="sm:col-span-2" />
        <select className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" value={form.category}
          onChange={e => setForm({ ...form, category: e.target.value })}>
          <option value="">Any category</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" value={form.city}
          onChange={e => setForm({ ...form, city: e.target.value })}>
          <option value="">Any city</option>
          {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <Input placeholder="Keyword (optional)" value={form.keyword} onChange={e => setForm({ ...form, keyword: e.target.value })} />
        <div className="flex gap-2">
          <select className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white" value={form.salary_type}
            onChange={e => setForm({ ...form, salary_type: e.target.value })}>
            <option value="yearly">LPA</option>
            <option value="monthly">Monthly</option>
          </select>
          <Input type="number" placeholder="Min salary" value={form.salary_min} onChange={e => setForm({ ...form, salary_min: e.target.value })} />
        </div>
        <div className="flex gap-2 sm:col-span-2">
          <Input type="number" min="0" placeholder="Min experience (yrs)" value={form.experience_min}
            onChange={e => setForm({ ...form, experience_min: e.target.value })} />
          <Input type="number" min="0" placeholder="Max experience (yrs, optional)" value={form.experience_max}
            onChange={e => setForm({ ...form, experience_max: e.target.value })} />
        </div>
      </div>
      <Button onClick={create} className="bg-black hover:bg-slate-800 text-white" data-testid="create-alert-btn">Create Alert</Button>

      <div className="mt-6 space-y-2">
        {loading ? (
          <p className="text-sm text-slate-400">Loading...</p>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-slate-400">No alerts yet — create one above.</p>
        ) : alerts.map(a => (
          <div key={a.id} className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-sm">
              <div className="font-medium text-slate-900">{a.label || "Job alert"}</div>
              <div className="text-slate-500 text-xs mt-0.5">{describe(a)}</div>
            </div>
            <button type="button" onClick={() => remove(a.id)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`delete-alert-${a.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SeekerDashboard() {
  const location = useLocation();
  const tab = (path) => location.pathname === path ? "bg-[#E8F0FE] text-[#1D6FF2]" : "text-slate-700 hover:bg-slate-100";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-[220px_1fr] gap-6" data-testid="seeker-dashboard">
      <aside className="md:sticky md:top-20 md:self-start">
        <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1">
          <Link to="/dashboard/seeker" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/seeker")}`}><User className="w-4 h-4 inline mr-2" />Profile</Link>
          <Link to="/dashboard/seeker/applications" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/seeker/applications")}`} data-testid="tab-applications"><FileText className="w-4 h-4 inline mr-2" />Applications</Link>
          <Link to="/dashboard/seeker/saved" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/seeker/saved")}`} data-testid="tab-saved"><Bookmark className="w-4 h-4 inline mr-2" />Saved</Link>
          <Link to="/dashboard/seeker/stats" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/seeker/stats")}`} data-testid="tab-seeker-stats"><BarChart3 className="w-4 h-4 inline mr-2" />Statistics</Link>
          <Link to="/dashboard/seeker/alerts" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/seeker/alerts")}`} data-testid="tab-alerts"><Bell className="w-4 h-4 inline mr-2" />Job Alerts</Link>
        </div>
      </aside>
      <Routes>
        <Route index element={<Profile />} />
        <Route path="applications" element={<Applications />} />
        <Route path="saved" element={<Saved />} />
        <Route path="stats" element={<SeekerStats />} />
        <Route path="alerts" element={<Alerts />} />
      </Routes>
    </div>
  );
}
